import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  CalendarCheck2,
  Clock3,
  MapPin,
  PhoneCall,
  ShieldCheck
} from 'lucide-react';
import SEO, { getLocalBusinessSchema, getWebsiteSchema } from '../components/SEO';
import SiteFooter from '../components/SiteFooter';
import SmartImage from '../components/SmartImage';
import AnimatedSection from '../components/AnimatedSection';
import { fadeInUp, staggerContainer, staggerContainerSlow, scaleIn, slideInLeft } from '../utils/animations';
import './HomePage.css';

function HomePage() {
  const navigate = useNavigate();

  const crew = [
    {
      name: 'Riza',
      role: 'Founding Barber',
      note: 'Known for precision, consistency, and neighborhood trust.',
      quote: 'Every cut is a conversation.',
      specialties: ['Classic Fades', 'Beard Shaping', 'Hot Towel'],
      image: '/images/barbers/riza.jpeg',
      fallbackSrc: '/images/barbers/john.jpg',
      featured: true
    },
    {
      name: 'Niki',
      role: 'Master Barber',
      note: 'Disciplined fades, beard structure, polished finishing.',
      quote: 'Details make the difference.',
      specialties: ['Skin Fades', 'Line Work', 'Beard Detail'],
      image: '/images/barbers/nick.jpeg',
      fallbackSrc: '/images/barbers/eric.jpg'
    },
    {
      name: 'Eric',
      role: 'Senior Barber',
      note: 'Steady hands, professional pace, shape that lasts.',
      quote: 'Consistency is the craft.',
      specialties: ['Tapers', 'Scissor Cuts', 'Clean Lines'],
      image: '/images/barbers/eric.jpg',
      fallbackSrc: '/images/barbers/al.jpeg'
    },
    {
      name: 'Cynthia',
      role: 'Aesthetics + Facials Specialist',
      note: 'Refined cuts with detail-led aesthetic and facial work.',
      quote: 'Style should feel effortless.',
      specialties: ['Facials', 'Aesthetic Cuts', 'Grooming'],
      image: '/images/barbers/cynthia.jpeg',
      fallbackSrc: '/images/barbers/riza.jpeg'
    }
  ];

  const quickFacts = [
    { icon: <MapPin size={15} />, label: '332 Barrow St · Downtown Jersey City' },
    { icon: <Clock3 size={15} />, label: 'Book in under a minute' },
    { icon: <CalendarCheck2 size={15} />, label: 'Live availability' },
    { icon: <ShieldCheck size={15} />, label: 'Nearly 30 years combined chair craft' }
  ];

  return (
    <>
      <SEO
        title="Home"
        description="Balkan Barber in Downtown Jersey City: Grove Street atmosphere, owner-led craftsmanship, and an image-first booking experience."
        schema={[getLocalBusinessSchema(), getWebsiteSchema()]}
      />

      <div className="home-page home-story page-wrap">
        <div className="jc-bg-wash" style={{ backgroundImage: "url('/images/library/neighborhood/neighborhood-jc-skyline-actual.webp')" }} aria-hidden="true" />

        {/* ── Section 1: Hero — Al's Portrait ── */}
        <section className="home-hero main-shell">
          <div className="home-hero__inner">
            <div className="home-hero__portrait">
              <SmartImage
                src="/images/barbers/al.jpeg"
                fallbackSrc="/images/barbers/riza.jpeg"
                alt="Al, owner and lead barber at Balkan Barber"
                className="home-hero__portrait-img"
                loading="eager"
              />
              <div className="home-hero__portrait-fade" />
            </div>

            <motion.div
              className="home-hero__content"
              initial="hidden"
              animate="visible"
              variants={staggerContainerSlow}
            >
              <motion.p className="home-hero__kicker" variants={fadeInUp}>
                Downtown Jersey City · Grove Street
              </motion.p>
              <motion.h1 variants={fadeInUp}>Balkan Barber</motion.h1>
              <motion.p className="home-hero__tagline" variants={fadeInUp}>
                Old-school style. New-school touch.
              </motion.p>
              <motion.p className="home-hero__address" variants={fadeInUp}>
                332 Barrow St · Grove Street · Downtown Jersey City
              </motion.p>
              <motion.div className="home-hero__actions" variants={fadeInUp}>
                <button className="btn btn-primary" onClick={() => navigate('/booking')}>
                  Book Appointment <ArrowRight size={16} />
                </button>
                <button className="btn btn-secondary" onClick={() => navigate('/about')}>
                  Meet The Team
                </button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ── Section 2: Quick Facts ── */}
        <AnimatedSection className="main-shell home-facts" variants={staggerContainer} as="section">
          {quickFacts.map((fact) => (
            <motion.div className="home-fact-pill" key={fact.label} variants={slideInLeft}>
              {fact.icon}
              <span>{fact.label}</span>
            </motion.div>
          ))}
        </AnimatedSection>

        {/* ── Section 3: The Crew — Asymmetric Editorial Grid ── */}
        <AnimatedSection className="main-shell page-section home-crew" as="section">
          <div className="home-crew__heading">
            <p className="home-story-kicker">The team story</p>
            <h2>Built by people who stayed.</h2>
            <p>Nearly 30 years of combined experience across a team customers trust.</p>
          </div>

          <motion.div
            className="home-crew__grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
          >
            {crew.map((member) => (
              <motion.article
                className={`home-crew-card ${member.featured ? 'home-crew-card--featured' : ''}`}
                key={member.name}
                variants={fadeInUp}
              >
                <SmartImage
                  src={member.image}
                  fallbackSrc={member.fallbackSrc}
                  alt={`${member.name} at Balkan Barber`}
                  className="home-crew-card__image"
                />
                <div className="home-crew-card__copy">
                  <h3>{member.name}</h3>
                  <p className="home-crew-card__role">{member.role}</p>
                  <p className="home-crew-card__note">{member.note}</p>
                  <div className="home-crew-card__tags">
                    {member.specialties.map((s) => (
                      <span className="home-crew-card__tag" key={s}>{s}</span>
                    ))}
                  </div>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </AnimatedSection>

        {/* ── Section 4: Neighborhood — Editorial Image + Text ── */}
        <AnimatedSection className="home-neighborhood page-section" variants={fadeInUp} as="section">
          <div className="jc-bg-wash jc-bg-wash--neighborhood" style={{ backgroundImage: "url('/images/library/neighborhood/neighborhood-animated-balkan-block-rainy-dusk.webp')" }} aria-hidden="true" />
          <div className="home-neighborhood__inner main-shell">
            <div className="home-neighborhood__text">
              <p className="home-story-kicker">The neighborhood</p>
              <h2>332 Barrow Street</h2>
              <p>
                In the heart of Downtown Jersey City, steps from Grove Street.
                Where the morning coffee crowd meets the after-work fade.
                A neighborhood barbershop with a reputation that travels.
              </p>
            </div>
            <div className="home-neighborhood__media">
              <SmartImage
                src="/images/library/neighborhood/neighborhood-evening-street-atmosphere.webp"
                fallbackSrc="/images/library/neighborhood/neighborhood-animated-grove-layout.webp"
                alt="Evening atmosphere near Grove Street, Downtown Jersey City"
                className="home-neighborhood__image"
              />
            </div>
          </div>
        </AnimatedSection>

        {/* ── Section 5: CTA ── */}
        <AnimatedSection className="main-shell page-section home-cta gradient-glow" variants={scaleIn} as="section">
          <div className="jc-bg-wash jc-bg-wash--cta" style={{ backgroundImage: "url('/images/library/neighborhood/neighborhood-grove-station-night-actual.webp')" }} aria-hidden="true" />
          <h2>Ready for the chair?</h2>
          <p>Choose your barber, lock your time, and walk in ready.</p>
          <div className="home-cta__actions">
            <button className="btn btn-primary" onClick={() => navigate('/booking')}>
              Book Now
            </button>
            <a href="tel:+12014332870" className="btn btn-secondary">
              <PhoneCall size={15} /> (201) 433-2870
            </a>
          </div>
        </AnimatedSection>

        <SiteFooter />
      </div>
    </>
  );
}

export default HomePage;
